import React, { useState } from 'react';
import { Row, Col, Card, Select, Typography, Tag, Space } from 'antd';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import {
    ThunderboltOutlined,
    RobotOutlined,
    SettingOutlined,
    GlobalOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const { Title, Text } = Typography;
const { Option } = Select;

// 修复Leaflet默认图标问题
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface StationData {
    id: string;
    name: string;
    lat: number;
    lng: number;
    aiAccuracy: number;
    traditionalAccuracy: number;
    aiSpeed: number;
    traditionalSpeed: number;
    region: string;
    type: 'wind' | 'solar' | 'hydro';
    betterModel: 'ai' | 'traditional';
}

interface RegionData {
    name: string;
    code: string;
    center: [number, number];
    zoom: number;
    flag: string;
}

const PredictionEvaluation: React.FC = () => {
    const [selectedRegion, setSelectedRegion] = useState<string>('china');

    // 地区配置
    const regions: RegionData[] = [
        { name: '中国南方', code: 'china', center: [23.5, 113.0], zoom: 6, flag: '🇨🇳' },
        { name: '越南', code: 'vietnam', center: [16.0, 106.0], zoom: 6, flag: '🇻🇳' },
        { name: '印度', code: 'india', center: [20.0, 77.0], zoom: 5, flag: '🇮🇳' },
        { name: '巴西', code: 'brazil', center: [-15.0, -47.0], zoom: 5, flag: '🇧🇷' },
    ];

    // 生成站点数据的辅助函数
    const generateStationData = () => {
        const stationTypes: ('wind' | 'solar' | 'hydro')[] = ['wind', 'solar', 'hydro'];
        const getRandomAccuracy = (base: number, variance: number) =>
            Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;
        const getRandomSpeed = (base: number, variance: number) =>
            Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;

        // 中国南方站点数据
        const chinaSouthStations: StationData[] = [];

        // 广东省站点（50个）
        const guangdongCities = [
            { name: '深圳', lat: 22.5, lng: 114.1 }, { name: '广州', lat: 23.1, lng: 113.3 },
            { name: '东莞', lat: 23.0, lng: 113.8 }, { name: '佛山', lat: 23.0, lng: 113.1 },
            { name: '中山', lat: 22.5, lng: 113.4 }, { name: '珠海', lat: 22.3, lng: 113.6 },
            { name: '江门', lat: 22.6, lng: 113.1 }, { name: '惠州', lat: 23.1, lng: 114.4 },
            { name: '汕头', lat: 23.4, lng: 116.7 }, { name: '湛江', lat: 21.3, lng: 110.4 }
        ];
        guangdongCities.forEach((city, index) => {
            for (let i = 0; i < 5; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const aiAcc = getRandomAccuracy(type === 'wind' ? 94 : type === 'solar' ? 92 : 89, 6);
                const tradAcc = getRandomAccuracy(type === 'wind' ? 85 : type === 'solar' ? 83 : 87, 8);
                const station: StationData = {
                    id: `GD${(index * 5 + i + 1).toString().padStart(3, '0')}`,
                    name: `${city.name}${type === 'wind' ? '风电' : type === 'solar' ? '光伏' : '水电'}场${i + 1}号`,
                    lat: city.lat + (Math.random() - 0.5) * 0.5,
                    lng: city.lng + (Math.random() - 0.5) * 0.5,
                    aiAccuracy: aiAcc,
                    traditionalAccuracy: tradAcc,
                    aiSpeed: getRandomSpeed(0.8, 0.6),
                    traditionalSpeed: getRandomSpeed(16, 8),
                    region: 'china',
                    type,
                    betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                };
                chinaSouthStations.push(station);
            }
        });

        // 广西站点（30个）
        const guangxiCities = [
            { name: '南宁', lat: 22.8, lng: 108.3 }, { name: '柳州', lat: 24.3, lng: 109.4 },
            { name: '桂林', lat: 25.3, lng: 110.3 }, { name: '梧州', lat: 23.5, lng: 111.3 },
            { name: '北海', lat: 21.5, lng: 109.1 }, { name: '钦州', lat: 21.9, lng: 108.6 }
        ];
        guangxiCities.forEach((city, index) => {
            for (let i = 0; i < 5; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const aiAcc = getRandomAccuracy(type === 'wind' ? 93 : type === 'solar' ? 91 : 90, 5);
                const tradAcc = getRandomAccuracy(type === 'wind' ? 84 : type === 'solar' ? 82 : 86, 7);
                const station: StationData = {
                    id: `GX${(index * 5 + i + 1).toString().padStart(3, '0')}`,
                    name: `${city.name}${type === 'wind' ? '风电' : type === 'solar' ? '光伏' : '水电'}站${i + 1}号`,
                    lat: city.lat + (Math.random() - 0.5) * 0.4,
                    lng: city.lng + (Math.random() - 0.5) * 0.4,
                    aiAccuracy: aiAcc,
                    traditionalAccuracy: tradAcc,
                    aiSpeed: getRandomSpeed(0.9, 0.5),
                    traditionalSpeed: getRandomSpeed(15, 6),
                    region: 'china',
                    type,
                    betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                };
                chinaSouthStations.push(station);
            }
        });

        // 福建站点（25个）
        const fujianCities = [
            { name: '福州', lat: 26.1, lng: 119.3 }, { name: '厦门', lat: 24.5, lng: 118.1 },
            { name: '泉州', lat: 24.9, lng: 118.6 }, { name: '漳州', lat: 24.5, lng: 117.6 },
            { name: '莆田', lat: 25.4, lng: 119.0 }
        ];
        fujianCities.forEach((city, index) => {
            for (let i = 0; i < 5; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const aiAcc = getRandomAccuracy(type === 'wind' ? 95 : type === 'solar' ? 93 : 88, 4);
                const tradAcc = getRandomAccuracy(type === 'wind' ? 86 : type === 'solar' ? 84 : 85, 6);
                const station: StationData = {
                    id: `FJ${(index * 5 + i + 1).toString().padStart(3, '0')}`,
                    name: `${city.name}${type === 'wind' ? '海上风电' : type === 'solar' ? '光伏' : '水电'}场${i + 1}号`,
                    lat: city.lat + (Math.random() - 0.5) * 0.3,
                    lng: city.lng + (Math.random() - 0.5) * 0.3,
                    aiAccuracy: aiAcc,
                    traditionalAccuracy: tradAcc,
                    aiSpeed: getRandomSpeed(0.7, 0.4),
                    traditionalSpeed: getRandomSpeed(17, 7),
                    region: 'china',
                    type,
                    betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                };
                chinaSouthStations.push(station);
            }
        });

        // 海南站点（15个）
        const hainanCities = [
            { name: '海口', lat: 20.0, lng: 110.3 }, { name: '三亚', lat: 18.2, lng: 109.5 },
            { name: '儋州', lat: 19.5, lng: 109.6 }
        ];
        hainanCities.forEach((city, index) => {
            for (let i = 0; i < 5; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const aiAcc = getRandomAccuracy(type === 'wind' ? 96 : type === 'solar' ? 94 : 87, 3);
                const tradAcc = getRandomAccuracy(type === 'wind' ? 87 : type === 'solar' ? 85 : 84, 5);
                const station: StationData = {
                    id: `HN${(index * 5 + i + 1).toString().padStart(3, '0')}`,
                    name: `${city.name}${type === 'wind' ? '海上风电' : type === 'solar' ? '光伏' : '小水电'}基地${i + 1}号`,
                    lat: city.lat + (Math.random() - 0.5) * 0.2,
                    lng: city.lng + (Math.random() - 0.5) * 0.2,
                    aiAccuracy: aiAcc,
                    traditionalAccuracy: tradAcc,
                    aiSpeed: getRandomSpeed(0.6, 0.3),
                    traditionalSpeed: getRandomSpeed(18, 5),
                    region: 'china',
                    type,
                    betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                };
                chinaSouthStations.push(station);
            }
        });

        // 云南贵州站点（40个）
        const yunguiCities = [
            { name: '昆明', lat: 25.0, lng: 102.7 }, { name: '大理', lat: 25.6, lng: 100.3 },
            { name: '丽江', lat: 26.9, lng: 100.2 }, { name: '贵阳', lat: 26.6, lng: 106.6 },
            { name: '遵义', lat: 27.7, lng: 106.9 }, { name: '安顺', lat: 26.2, lng: 105.9 },
            { name: '曲靖', lat: 25.5, lng: 103.8 }, { name: '红河', lat: 23.4, lng: 103.4 }
        ];
        yunguiCities.forEach((city, index) => {
            for (let i = 0; i < 5; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const aiAcc = getRandomAccuracy(type === 'wind' ? 92 : type === 'solar' ? 93 : 91, 5);
                const tradAcc = getRandomAccuracy(type === 'wind' ? 83 : type === 'solar' ? 84 : 88, 7);
                const station: StationData = {
                    id: `YG${(index * 5 + i + 1).toString().padStart(3, '0')}`,
                    name: `${city.name}${type === 'wind' ? '风电' : type === 'solar' ? '高原光伏' : '水电'}站${i + 1}号`,
                    lat: city.lat + (Math.random() - 0.5) * 0.4,
                    lng: city.lng + (Math.random() - 0.5) * 0.4,
                    aiAccuracy: aiAcc,
                    traditionalAccuracy: tradAcc,
                    aiSpeed: getRandomSpeed(1.0, 0.6),
                    traditionalSpeed: getRandomSpeed(14, 8),
                    region: 'china',
                    type,
                    betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                };
                chinaSouthStations.push(station);
            }
        });

        return chinaSouthStations;
    };

    // 生成越南站点数据
    const generateVietnamStations = () => {
        const vietnamStations: StationData[] = [];
        const stationTypes: ('wind' | 'solar' | 'hydro')[] = ['wind', 'solar', 'hydro'];
        const getRandomAccuracy = (base: number, variance: number) =>
            Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;
        const getRandomSpeed = (base: number, variance: number) =>
            Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;

        // 越南各省市（80个站点）
        const vietnamRegions = [
            { name: '胡志明市', lat: 10.8, lng: 106.7, prefix: 'HCM' },
            { name: '河内', lat: 21.0, lng: 105.8, prefix: 'HN' },
            { name: '岘港', lat: 16.1, lng: 108.2, prefix: 'DN' },
            { name: '平顺', lat: 10.9, lng: 108.1, prefix: 'PT' },
            { name: '宁顺', lat: 11.6, lng: 108.9, prefix: 'NT' },
            { name: '金瓯', lat: 9.2, lng: 105.1, prefix: 'CA' },
            { name: '同奈', lat: 10.9, lng: 107.2, prefix: 'DN2' },
            { name: '西宁', lat: 11.0, lng: 106.1, prefix: 'TN' },
            { name: '富安', lat: 13.2, lng: 109.3, prefix: 'PA' },
            { name: '庆和', lat: 12.2, lng: 109.2, prefix: 'KH' },
            { name: '广义', lat: 15.1, lng: 108.8, prefix: 'QN' },
            { name: '承天', lat: 16.5, lng: 107.6, prefix: 'TT' },
            { name: '河静', lat: 18.3, lng: 105.9, prefix: 'HA' },
            { name: '义安', lat: 19.2, lng: 105.6, prefix: 'NA' },
            { name: '清化', lat: 19.8, lng: 105.8, prefix: 'TH' },
            { name: '广宁', lat: 21.0, lng: 107.3, prefix: 'QN2' }
        ];

        vietnamRegions.forEach((region, regionIndex) => {
            for (let i = 0; i < 5; i++) {
                const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                const aiAcc = getRandomAccuracy(type === 'wind' ? 91 : type === 'solar' ? 89 : 87, 6);
                const tradAcc = getRandomAccuracy(type === 'wind' ? 82 : type === 'solar' ? 80 : 84, 8);
                const station: StationData = {
                    id: `VN${region.prefix}${(i + 1).toString().padStart(2, '0')}`,
                    name: `${region.name}${type === 'wind' ? '风电' : type === 'solar' ? '光伏' : '水电'}场${i + 1}号`,
                    lat: region.lat + (Math.random() - 0.5) * 0.3,
                    lng: region.lng + (Math.random() - 0.5) * 0.3,
                    aiAccuracy: aiAcc,
                    traditionalAccuracy: tradAcc,
                    aiSpeed: getRandomSpeed(1.1, 0.7),
                    traditionalSpeed: getRandomSpeed(19, 8),
                    region: 'vietnam',
                    type,
                    betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                };
                vietnamStations.push(station);
            }
        });

        return vietnamStations;
    };

    // 模拟站点数据
    const stationsData: { [key: string]: StationData[] } = {
        china: generateStationData(),
        vietnam: generateVietnamStations(),
        india: (() => {
            const indiaStations: StationData[] = [];
            const stationTypes: ('wind' | 'solar' | 'hydro')[] = ['wind', 'solar', 'hydro'];
            const getRandomAccuracy = (base: number, variance: number) =>
                Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;
            const getRandomSpeed = (base: number, variance: number) =>
                Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;

            const indiaRegions = [
                { name: '拉贾斯坦', lat: 27.0, lng: 74.2 }, { name: '古吉拉特', lat: 23.0, lng: 72.6 },
                { name: '马哈拉施特拉', lat: 19.8, lng: 75.3 }, { name: '卡纳塔克', lat: 15.3, lng: 75.7 },
                { name: '安得拉邦', lat: 15.9, lng: 79.7 }, { name: '泰米尔纳德', lat: 11.1, lng: 78.7 }
            ];

            indiaRegions.forEach((region, index) => {
                for (let i = 0; i < 8; i++) {
                    const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                    const aiAcc = getRandomAccuracy(type === 'wind' ? 90 : type === 'solar' ? 88 : 85, 6);
                    const tradAcc = getRandomAccuracy(type === 'wind' ? 81 : type === 'solar' ? 79 : 82, 7);
                    const station: StationData = {
                        id: `IN${(index * 8 + i + 1).toString().padStart(3, '0')}`,
                        name: `${region.name}${type === 'wind' ? '风电' : type === 'solar' ? '光伏' : '水电'}园区${i + 1}号`,
                        lat: region.lat + (Math.random() - 0.5) * 0.8,
                        lng: region.lng + (Math.random() - 0.5) * 0.8,
                        aiAccuracy: aiAcc,
                        traditionalAccuracy: tradAcc,
                        aiSpeed: getRandomSpeed(1.0, 0.6),
                        traditionalSpeed: getRandomSpeed(18, 7),
                        region: 'india',
                        type,
                        betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                    };
                    indiaStations.push(station);
                }
            });
            return indiaStations;
        })(),
        brazil: (() => {
            const brazilStations: StationData[] = [];
            const stationTypes: ('wind' | 'solar' | 'hydro')[] = ['wind', 'solar', 'hydro'];
            const getRandomAccuracy = (base: number, variance: number) =>
                Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;
            const getRandomSpeed = (base: number, variance: number) =>
                Math.round((base + (Math.random() - 0.5) * variance) * 10) / 10;

            const brazilRegions = [
                { name: '塞阿拉', lat: -4.5, lng: -37.8 }, { name: '米纳斯吉拉斯', lat: -18.1, lng: -44.3 },
                { name: '巴伊亚', lat: -12.9, lng: -38.5 }, { name: '圣保罗', lat: -23.5, lng: -46.6 },
                { name: '里约热内卢', lat: -22.9, lng: -43.2 }, { name: '南里奥格兰德', lat: -30.0, lng: -51.2 }
            ];

            brazilRegions.forEach((region, index) => {
                for (let i = 0; i < 6; i++) {
                    const type = stationTypes[Math.floor(Math.random() * stationTypes.length)];
                    const aiAcc = getRandomAccuracy(type === 'wind' ? 91 : type === 'solar' ? 89 : 87, 5);
                    const tradAcc = getRandomAccuracy(type === 'wind' ? 82 : type === 'solar' ? 80 : 84, 6);
                    const station: StationData = {
                        id: `BR${(index * 6 + i + 1).toString().padStart(3, '0')}`,
                        name: `${region.name}${type === 'wind' ? '风电' : type === 'solar' ? '光伏' : '水电'}中心${i + 1}号`,
                        lat: region.lat + (Math.random() - 0.5) * 0.6,
                        lng: region.lng + (Math.random() - 0.5) * 0.6,
                        aiAccuracy: aiAcc,
                        traditionalAccuracy: tradAcc,
                        aiSpeed: getRandomSpeed(0.9, 0.5),
                        traditionalSpeed: getRandomSpeed(17, 6),
                        region: 'brazil',
                        type,
                        betterModel: aiAcc > tradAcc ? 'ai' : 'traditional'
                    };
                    brazilStations.push(station);
                }
            });
            return brazilStations;
        })()
    };

    const currentStations = stationsData[selectedRegion] || [];
    const currentRegion = regions.find(r => r.code === selectedRegion) || regions[0];

    // 全局统计
    const totalAllStations = Object.values(stationsData).reduce((sum, stations) => sum + stations.length, 0);
    const allStations = Object.values(stationsData).flat();
    const totalAiWins = allStations.filter(s => s.betterModel === 'ai').length;



    // 统计数据
    const aiWinCount = currentStations.filter(s => s.betterModel === 'ai').length;
    const totalStations = currentStations.length;
    const avgAccuracyImprovement = currentStations.reduce((sum, s) =>
        sum + (s.aiAccuracy - s.traditionalAccuracy), 0) / totalStations;
    const avgSpeedImprovement = currentStations.reduce((sum, s) =>
        sum + (s.traditionalSpeed / s.aiSpeed), 0) / totalStations;



    // 获取标记颜色
    const getMarkerColor = (station: StationData) => {
        if (station.betterModel === 'ai') {
            return '#1890ff'; // 蓝色表示大模型更好
        } else {
            return '#fa8c16'; // 橙色表示传统模型更好
        }
    };

    return (
        <div className="page-transition">
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title level={2} style={{ margin: 0 }}>
                        <RobotOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        预测评定
                    </Title>
                    <Space>
                        <Select
                            value={selectedRegion}
                            onChange={setSelectedRegion}
                            style={{ width: 140 }}
                            placeholder="选择地区"
                        >
                            {regions.map(region => (
                                <Option key={region.code} value={region.code}>
                                    <span style={{ marginRight: '6px', fontSize: '16px' }}>{region.flag}</span>
                                    {region.name}
                                </Option>
                            ))}
                        </Select>
                    </Space>
                </div>
                <div style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, #e6f7ff 0%, #f6ffed 100%)',
                    borderRadius: '6px',
                    border: '1px solid #b7eb8f'
                }}>
                    <Text style={{ fontSize: '14px', color: '#52c41a' }}>
                        🌍 全球总览：共覆盖 <strong>{totalAllStations}</strong> 个电站，
                        大模型在 <strong>{totalAiWins}</strong> 个站点表现更优，
                        优势率达 <strong>{Math.round(totalAiWins / totalAllStations * 100)}%</strong>
                        {' | ' + currentRegion.flag + ' 当前展示' + currentRegion.name + '数据'}
                    </Text>
                </div>
            </div>

            {/* 统计概览卡片 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <RobotOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '8px' }} />
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                                {aiWinCount}/{totalStations}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>大模型优势站点</div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                +{avgAccuracyImprovement.toFixed(1)}%
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>平均精度提升</div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <ClockCircleOutlined style={{ fontSize: '32px', color: '#fa8c16', marginBottom: '8px' }} />
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                                {avgSpeedImprovement.toFixed(1)}x
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>平均速度提升</div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: 'center' }}>
                            <GlobalOutlined style={{ fontSize: '32px', color: '#722ed1', marginBottom: '8px' }} />
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                                {currentStations.length}
                            </div>
                            <div style={{ color: '#666', fontSize: '14px' }}>总站点数 ({currentRegion.name})</div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* 地图展示区域 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col span={24}>
                    <Card
                        title={
                            <Space>
                                <GlobalOutlined />
                                {currentRegion.name}地区站点分布
                                <Tag color="blue">蓝色：大模型</Tag>
                                <Tag color="orange">橙色：传统模型</Tag>
                            </Space>
                        }
                        bodyStyle={{ padding: '12px' }}
                    >
                        <div style={{ height: '800px' }}>
                            <MapContainer
                                center={currentRegion.center}
                                zoom={currentRegion.zoom}
                                style={{ height: '100%', width: '100%' }}
                                key={selectedRegion} // 强制重新渲染地图
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                {currentStations.map(station => (
                                    <CircleMarker
                                        key={station.id}
                                        center={[station.lat, station.lng]}
                                        radius={8}
                                        fillColor={getMarkerColor(station)}
                                        color="#fff"
                                        weight={2}
                                        opacity={1}
                                        fillOpacity={0.8}
                                    >
                                        <Popup>
                                            <div style={{ minWidth: '200px' }}>
                                                <h4>{station.name}</h4>
                                                <p><strong>类型:</strong> {
                                                    station.type === 'wind' ? '风电' :
                                                        station.type === 'solar' ? '光伏' : '水电'
                                                }</p>
                                                <p><strong>大模型准确率:</strong> {station.aiAccuracy}%</p>
                                                <p><strong>传统模型准确率:</strong> {station.traditionalAccuracy}%</p>
                                                <p><strong>精度提升:</strong>
                                                    <span style={{ color: station.aiAccuracy > station.traditionalAccuracy ? '#52c41a' : '#ff4d4f' }}>
                                                        {(station.aiAccuracy - station.traditionalAccuracy).toFixed(1)}%
                                                    </span>
                                                </p>
                                                <p><strong>速度提升:</strong>
                                                    <span style={{ color: '#1890ff' }}>
                                                        {(station.traditionalSpeed / station.aiSpeed).toFixed(1)}x
                                                    </span>
                                                </p>
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                        </div>
                    </Card>
                </Col>
            </Row>


        </div>
    );
};

export default PredictionEvaluation;